// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import styles from './PaperSelector.module.css';

/** Canvas ground. The paper-TINT axis (light mode) is white/yellow/green; 'black'
 *  is the separate dark-mode axis (its toggle lives in Settings/CO-3, not here). */
export type CanvasPaper = 'white' | 'yellow' | 'green' | 'black';

/** The three light-mode paper tints, in cycle order (Will 08-06 locked values:
 *  white default -> very-light yellow -> very-light green). */
const TINTS: ReadonlyArray<{ id: CanvasPaper; label: string; swatch: string }> = [
  { id: 'white', label: 'White', swatch: '#ffffff' },
  { id: 'yellow', label: 'Yellow', swatch: '#fffef8' },
  { id: 'green', label: 'Green', swatch: '#f7fcf4' },
];

/** Next tint in the white -> cream -> yellow -> white cycle. A non-tint value
 *  (e.g. dark-mode 'black') cycles back to the white default. */
export function nextPaperTint(paper: CanvasPaper): CanvasPaper {
  const i = TINTS.findIndex((t) => t.id === paper);
  return TINTS[(i + 1) % TINTS.length].id;
}

interface PaperSelectorProps {
  paper: CanvasPaper;
  onChange: (paper: CanvasPaper) => void;
}

/**
 * The canvas paper-tint control (WS8a) -- a single circle you click to cycle
 * white -> cream -> yellow (Will 08-03: space-saving, one control not three
 * swatches). It is the paper-TINT axis only; the light/dark toggle is a separate
 * control in Settings (CO-3). Present in WS8a because the tint is notation-adjacent
 * and the true-white default is a WS8a acceptance item.
 *
 * Presentation-only: `paper` is a prop; the cycle flows back through `onChange`,
 * so the Editor stays the single source of truth.
 */
export class PaperSelector extends React.PureComponent<PaperSelectorProps> {
  render(): React.ReactNode {
    const { paper, onChange } = this.props;
    const current = TINTS.find((t) => t.id === paper) ?? TINTS[0];
    const next = TINTS[(TINTS.findIndex((t) => t.id === current.id) + 1) % TINTS.length];
    return (
      <button
        type="button"
        className={styles.swatchButton}
        aria-label={`Canvas paper: ${current.label}. Click for ${next.label}.`}
        title={`Paper: ${current.label} — click to cycle`}
        onClick={() => onChange(nextPaperTint(paper))}
      >
        <span className={styles.swatch} style={{ background: current.swatch }} />
      </button>
    );
  }
}
